import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useLang } from '../i18n'
import {
  cancelIndex,
  createFolder,
  deIndex,
  GraphError,
  indexJob,
  listChildren,
  listLibraries,
  requestAccessUrl,
  resolveLink,
  searchSites,
  startIndex,
  type SpEntry,
  type SpIndexJob,
  type SpIndexProgressEntry,
  type SpIndexSelection,
  type SpResolved,
  type SpSite,
} from '../lib/graphSites'
import { FileIcon } from './FileIcons'
import { IconFolder, IconSearch } from './NavIcons'
import { ConfirmModal } from './ConfirmModal'
import { IndexResultModal } from './IndexResultModal'
import { PromptModal } from './PromptModal'

/**
 * SharePoint site / library / folder browser, in two modes.
 *
 * - `select` — search a site, walk into a library and its folders, confirm the location. Used by
 *   the bid-site picker dialog.
 * - `browse` — pinned to ONE location (the bid's site) and opened there. No site search: the
 *   member is looking at the bid's own material, not choosing a new home for it. With
 *   `rootAnchored` (the dashboard pane) the pinned folder is also the TOP: the trail starts there
 *   rather than at the site, and an "up one level" row in the list stops there too.
 *
 * Both read SharePoint through NIOBE'S BACKEND (lib/graphSites.ts), which calls Graph on the
 * member's behalf — so only what they could open in SharePoint appears here, exactly as when this
 * pane held a Graph token of its own. A 403 is therefore an ordinary answer, not a fault: it means
 * this member has no access, and the only fix is a SharePoint permission — so we hand off to
 * SharePoint's own request-access page.
 *
 * There is nothing to sign in to. The pane opens and loads; it no longer negotiates a Microsoft 365
 * session first, cannot navigate the workspace away to Entra, and therefore has no state between
 * "mounted" and "listing" for the member to sit in.
 *
 * SIZE IS THE CALLER'S. This component fills the box it is given and scrolls its list inside it, so
 * walking into a folder of 200 files cannot resize the page around it. See {@link spbHeightForRows}.
 *
 * Reuses the shape of dealtracker's SharePointPicker (`select`/`browse`, site gallery + breadcrumb
 * + item list, request-access hand-off) without its code, which is Fluent UI and cannot be lifted
 * into a project that has neither the dependency nor the design language.
 *
 * `browse` mode also offers CLICK-TO-INDEX (`canIndex`), itself split into two LOCAL sub-modes
 * (`browserMode`, independent of the `select`/`browse` prop-level `mode` above — no conflict):
 * plain browsing (the default, no checkboxes) and index mode, entered via "Manage bid index".
 * Index mode offers a checkbox per row — a selection that now SURVIVES navigating between folders
 * — and four actions: index the selection (a file as itself, a folder walked recursively by the
 * backend, into the bid's own KB via `POST /v1/bids/:bid/sharepoint/index`, then polled and
 * cancellable mid-run), discard the selection, un-index it, or leave index mode ("Done"). Separate
 * from the admin-run corporate sync pipeline: this is one member, their own On-Behalf-Of token, and
 * whatever they just selected while looking at their material.
 */

/** One list row, in px — kept in step with `.spb-row` in styles.css, which fixes its height. */
export const SPB_ROW_PX = 28
/** The trail bar plus the list's own padding: everything in the pane that is NOT a row. */
export const SPB_CHROME_PX = 42

/** The height a `browse` pane needs to show exactly `rows` rows. */
export function spbHeightForRows(rows: number): number {
  return Math.round(rows * SPB_ROW_PX + SPB_CHROME_PX)
}

/** One level of the trail. `driveId: null` is the site's list of libraries. */
interface Crumb {
  /** Path segment(s) this crumb adds — joined by {@link currentLocation} into the folder path. */
  name: string
  /** What a `rootAnchored` trail shows instead of `name`: the pinned folder's last segment. */
  label?: string
  webUrl: string
  driveId: string | null
  /** null at the library level, 'root' for a library's top, else the folder's drive-item id. */
  itemId: string | null
}

/**
 * Where the browser currently is — what `select` would pin, and what `browse` links to.
 *
 * No `driveId` (and so no `driveName`/`itemId`) means the SITE itself is the location: a site-only
 * pin, whose pane lists the site's libraries. Only `select` ever reports one — a `browse` pane with
 * `onSelect` (the export destination picker) is choosing somewhere to WRITE, which must be a
 * library or a folder.
 */
export interface BrowserLocation {
  site: SpSite
  driveId?: string
  driveName?: string
  itemId?: string
  /** Path under the library, '' at its root (and at the site level). */
  folderPath: string
  webUrl: string
}

/**
 * Where a `browse` browser opens: the stored pointer, already resolved to Graph ids. Partial by
 * depth, like the pin itself (lib/api.ts#BidSite): no `driveId` = the site's libraries.
 */
export interface BrowseStart {
  siteId: string
  siteName: string
  siteUrl: string
  driveId?: string
  driveName?: string
  itemId?: string
  folderPath: string
  webUrl: string
}

/** A pasted address rather than a search term. Whether it is SharePoint at all is the server's call. */
const LINK = /^https?:\/\//i

/**
 * The trail that opens AT a known location: `[site]`, `[site, library]` or `[site, library, folder]`.
 *
 * A folder is ONE crumb whatever its depth: the intermediate folders' ids were never known (a
 * stored pin keeps only the folder's own; a resolved link names only its target), so they cannot be
 * walked back to. The crumb's `name` is the whole path — {@link currentLocation} joins crumbs from
 * index 2 into the folder path, so a deeper navigation still reports `General/RFP/Annexes` — and
 * its `label` is the folder's own name for the panes that show the short form. Going up to the
 * library (and, unless anchored, to the site) stays possible.
 */
function trailTo(at: {
  siteName: string
  siteUrl: string
  driveId?: string
  driveName?: string
  driveUrl: string
  itemId?: string
  folderPath: string
  folderUrl: string
}): Crumb[] {
  const trail: Crumb[] = [{ name: at.siteName, webUrl: at.siteUrl, driveId: null, itemId: null }]
  if (!at.driveId || !at.itemId) return trail
  trail.push({ name: at.driveName ?? '', webUrl: at.driveUrl, driveId: at.driveId, itemId: 'root' })
  if (at.itemId !== 'root' && at.folderPath) {
    trail.push({
      name: at.folderPath,
      // Its own name, not the way down to it: "ACME", never "Bids/2026/ACME".
      label: at.folderPath.split('/').filter(Boolean).pop() ?? at.folderPath,
      webUrl: at.folderUrl,
      driveId: at.driveId,
      itemId: at.itemId,
    })
  }
  return trail
}

/** The trail a stored pin opens on. The library crumb links to the pin's own address, as before. */
function trailFromStart(start: BrowseStart): Crumb[] {
  return trailTo({
    siteName: start.siteName || start.siteId,
    siteUrl: start.siteUrl,
    driveId: start.driveId,
    driveName: start.driveName,
    driveUrl: start.webUrl,
    itemId: start.itemId,
    folderPath: start.folderPath,
    folderUrl: start.webUrl,
  })
}

/** The trail a resolved link opens on — as deep as the link went, and no deeper. */
function trailFromResolved(r: SpResolved): Crumb[] {
  const folderPath = r.item && r.item.id !== 'root' ? r.folderPath || r.item.name : ''
  return trailTo({
    siteName: r.site.displayName || r.site.id,
    siteUrl: r.site.webUrl,
    driveId: r.drive?.id,
    driveName: r.drive?.name,
    driveUrl: r.drive?.webUrl ?? r.site.webUrl,
    itemId: r.drive ? (r.item?.id ?? 'root') : undefined,
    folderPath,
    folderUrl: r.item?.webUrl ?? r.drive?.webUrl ?? r.site.webUrl,
  })
}

export function SharePointBrowser({
  bid,
  mode,
  start,
  rootAnchored = false,
  busy = false,
  canIndex = false,
  canCreateFolder = false,
  upRow = false,
  onPickFile,
  pickFileTitle,
  footer,
  onSelect,
  onCancel,
  onIndexed,
}: {
  /**
   * The bid this browsing is for. Not decoration: every read is `/v1/bids/:bid/sharepoint/*`, and
   * membership of THIS bid is what authorises it. A browser with no bid would be asking Niobe to
   * search SharePoint on no one's behalf in particular.
   */
  bid: string
  mode: 'select' | 'browse'
  /** Required in `browse` mode; ignored in `select`. */
  start?: BrowseStart
  /**
   * `browse` only — treat the pinned folder as the TOP of this pane.
   *
   * The site and the library (and anything above the pinned folder) stay in state, because the
   * drive id, the site and the folder path are all still read from there — they simply stop being
   * rendered as crumbs, and an "up one level" row appears in the list that stops at the pinned
   * folder rather than walking out of the bid's own material. Every crumb is clickable: the
   * earlier ones walk back to their folder, the current one opens it in SharePoint (so the trail
   * carries no separate "Open in SharePoint" link).
   *
   * Off by default, and deliberately not implied by `browse`: the export destination picker
   * (ExportMenu.tsx) is also a `browse` pane, and it shows the whole site › library › folder trail
   * because what it is choosing is a location, spelled out in full before it is confirmed.
   */
  rootAnchored?: boolean
  /** Disables the confirm button while the caller saves. */
  busy?: boolean
  /**
   * `browse` only — offers a checkbox per row plus an "Index selected" action that sends the
   * member's pick into the bid's own KB. The server enforces editor/admin (`canEditBid`); this
   * only avoids offering a control that would 403. Off by default: the `select` picker is for
   * choosing where the bid's material LIVES, a different write entirely.
   */
  canIndex?: boolean
  /**
   * Offers a "+ New folder" control in the crumb bar, enabled once inside an actual drive/folder.
   * Added for the export destination picker (`browse` + `onSelect`, no `canIndex`) — creating the
   * bid's `niobe/exports` folder, or a sibling of it, without leaving this pane. Server-enforced
   * editor/admin, same as `canIndex`; this only avoids offering a control that would 403.
   */
  canCreateFolder?: boolean
  /**
   * Heads EVERY listing below the site's own libraries with an "up one level" row — not only the
   * ones under a `rootAnchored` pin. Added for the export destination picker (AB#414), which opens
   * on whatever folder was chosen last and must be able to climb out of it one level at a time. A
   * folder crumb that is a whole path (a pin or a reopened destination, see {@link trailTo}) goes
   * up to its real parent folder, resolved from its address, not straight to the library.
   */
  upRow?: boolean
  /**
   * A file row becomes a button that hands the file to the caller instead of a link that opens it
   * in SharePoint. The export destination picker (AB#414) uses it to take an existing document's
   * name as the export's file name — the way to overwrite, or reuse, a name already there. The
   * PowerPoint import (AB#442) takes the file itself, so the drive it is in comes along.
   */
  onPickFile?: (entry: SpEntry, driveId: string) => void
  /** The picked file row's tooltip — what a click does (default: take the file's name). */
  pickFileTitle?: string
  /** Rendered between the list and the confirm bar — the export picker's file-name field. */
  footer?: ReactNode
  /**
   * The member confirmed this location. Historically `select`-only; also honoured in `browse` mode
   * when supplied, which turns on the same confirm/cancel bar — the export destination picker is a
   * `browse` pane (pinned to the bid's site) that still needs a "use this folder" action.
   */
  onSelect?: (location: BrowserLocation) => void
  /** Rendered as the dialog's cancel button whenever the confirm bar shows (see `onSelect`). */
  onCancel?: () => void
  /** `browse` + `canIndex` only — a click-to-index job just finished (done or error), so anything
   *  listing the bid's indexed sources (e.g. the Knowledge tab) may want to refresh. */
  onIndexed?: () => void
}) {
  const { t } = useLang()
  const isBrowse = mode === 'browse'

  const [search, setSearch] = useState('')
  const [sites, setSites] = useState<SpSite[]>([])
  const [sitesBusy, setSitesBusy] = useState(false)
  const [site, setSite] = useState<SpSite | null>(null)

  const [crumbs, setCrumbs] = useState<Crumb[]>([])
  const [entries, setEntries] = useState<SpEntry[]>([])
  const [itemsBusy, setItemsBusy] = useState(false)
  const [error, setError] = useState('')
  /** SharePoint — not Niobe — refused this location, and only SharePoint can grant it. */
  const [denied, setDenied] = useState(false)

  // Click-to-index (`canIndex`) lives in its own LOCAL sub-mode, entered via "Manage bid index"
  // and left via "Done" — checkboxes and the four-button bar below only ever render in `'index'`.
  // While in `'index'`, the selection SURVIVES navigation (the three loaders below only clear it
  // in `'browse'`) — the whole point of the mode split (plans/BID_SITE_INDEXING.md) is picking
  // items across folders before acting on them.
  const [browserMode, setBrowserMode] = useState<'browse' | 'index'>('browse')
  // Keyed by itemId, valued with the full descriptor: `entries` holds only the CURRENTLY open
  // folder, so once a folder is left, its id alone would be nothing to look a name/isFolder up
  // from — and both travel in the click-to-index/de-index request body.
  const [checked, setChecked] = useState<Map<string, SpIndexSelection>>(new Map())
  const [indexJobId, setIndexJobId] = useState<string | null>(null)
  const [indexStatus, setIndexStatus] = useState<SpIndexJob | null>(null)
  const [indexError, setIndexError] = useState('')
  // A snapshot of `checked` taken the moment a run starts (which itself clears `checked`, so the
  // member can immediately build a next selection) — the ids a per-row progress lookup keys off,
  // by id rather than by name, so a same-named file glimpsed in an unrelated folder never borrows
  // this run's outcome for itself.
  const [indexingIds, setIndexingIds] = useState<Set<string>>(new Set())
  const [showResultModal, setShowResultModal] = useState(false)
  const [cancelBusy, setCancelBusy] = useState(false)
  const [cancelError, setCancelError] = useState('')
  const pollTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // De-index — the opposite write, same checkboxes. Synchronous (no job to poll): the walk has no
  // download/parse/embed behind it. Confirmed once for the whole selection before firing.
  const [confirmingDeIndex, setConfirmingDeIndex] = useState(false)
  const [deIndexBusy, setDeIndexBusy] = useState(false)
  const [deIndexResult, setDeIndexResult] = useState<number | null>(null)
  const [deIndexError, setDeIndexError] = useState('')

  // "+ New folder" (canCreateFolder) — its own PromptModal, opened from the crumb bar.
  const [creatingFolder, setCreatingFolder] = useState(false)

  useEffect(() => () => clearTimeout(pollTimer.current), [])

  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  /**
   * A failure the member can act on.
   *
   * The hand-off keys on the CODE, never on the bare 403, because two different refusals wear that
   * status. `sharepoint_forbidden` is SharePoint's own verdict on a member of this bid — "not
   * shared with you" — and the only fix is a SharePoint permission, so we offer its request-access
   * page. A plain `forbidden` is Niobe refusing: the caller is not on this bid at all. Sending
   * them to SharePoint would ask for a permission they may already hold and would say nothing
   * about why they were really turned away, so it is stated instead.
   *
   * Anything else is reported as itself rather than swallowed. An outage must never render as an
   * empty folder, which would be a lie about what is in it.
   */
  const fail = useCallback(
    (e: unknown): void => {
      if (e instanceof GraphError && e.code === 'sharepoint_forbidden') {
        setDenied(true)
        return
      }
      // Named in the member's own language where the backend told us WHICH failure it was. Relaying
      // `serverMessage` for all of them left a French member reading English for every outage —
      // while the denied screen right above was translated. The code carries the same distinction
      // the message did, so nothing is lost by translating it.
      if (e instanceof GraphError) {
        const known: Record<string, string> = {
          sharepoint_unreachable: t('bidsite_unreachable'),
          not_found: t('bidsite_gone'),
          forbidden: t('bidsite_not_member'),
          // "+ New folder" (canCreateFolder) — a name collision or a name SharePoint will not accept.
          sharepoint_conflict: t('bidsite_folder_conflict'),
          invalid: t('bidsite_folder_invalid'),
        }
        setError(known[e.code] ?? e.message)
        return
      }
      setError(e instanceof Error ? e.message : String(e))
    },
    [t],
  )

  /** What is AT a crumb: a library's or folder's contents, or — at the site level — its libraries. */
  const listAt = useCallback(
    (siteId: string, crumb: Crumb): Promise<SpEntry[]> =>
      crumb.driveId && crumb.itemId
        ? listChildren(bid, crumb.driveId, crumb.itemId)
        : listLibraries(bid, siteId),
    [bid],
  )

  // browse: open straight at the stored location — the site's libraries, a library's top level or
  // a folder, as deep as the pin went. The ids are persisted precisely so this is one call — no
  // resolving a URL back into a site, a library and a chain of folders.
  useEffect(() => {
    if (!isBrowse || !start) return
    let cancelled = false
    setSite({ id: start.siteId, displayName: start.siteName, webUrl: start.siteUrl })
    // A different pinned folder is not a navigation within one — every bit of index-mode state
    // below belongs to the OLD location and would be meaningless (or, for a drive id, actively
    // wrong) against the new one.
    setBrowserMode('browse')
    setChecked(new Map())
    setDeIndexResult(null)
    setDeIndexError('')
    // The pinned folder is ONE crumb whatever its depth: the intermediate folders were never
    // stored, and walking back up past the bid's own folder is not what this pane is for.
    const trail = trailFromStart(start)
    setCrumbs(trail)
    setItemsBusy(true)
    listAt(start.siteId, trail[trail.length - 1]!)
      .then((children) => {
        if (!cancelled) setEntries(children)
      })
      .catch((e) => {
        if (!cancelled) fail(e)
      })
      .finally(() => {
        if (!cancelled) setItemsBusy(false)
      })
    return () => {
      cancelled = true
    }
  }, [isBrowse, start, fail, listAt])

  /**
   * Open the picker AT a pasted link's location, with the trail above it built so the member can
   * go up (to the library, to the site's libraries) or deeper before confirming.
   */
  const openResolved = useCallback(
    (r: SpResolved): void => {
      setSite(r.site)
      setSites([r.site])
      const trail = trailFromResolved(r)
      setCrumbs(trail)
      setItemsBusy(true)
      listAt(r.site.id, trail[trail.length - 1]!)
        .then(setEntries)
        .catch(fail)
        .finally(() => setItemsBusy(false))
    },
    [listAt, fail],
  )

  // select: debounced site search. Graph has no delegated "list every site", so an empty box
  // legitimately shows nothing until the member types.
  //
  // A pasted http(s) address is not a search term: it is RESOLVED (as the member) to the deepest
  // level it names, and the picker opens there. Whether it is a SharePoint address at all is the
  // server's call — a link it cannot open answers `null`, which is said in words, never shown as
  // an empty list that would read as "nothing there".
  useEffect(() => {
    if (isBrowse) return
    clearTimeout(debounce.current)
    const term = search.trim()
    if (!term) {
      setSites([])
      setSitesBusy(false)
      return
    }
    let live = true
    setSitesBusy(true)
    // Cleared per search, like every other navigation here: without it a failed search leaves its
    // alert standing over the successful one that follows, and — because the listing's empty state
    // is suppressed while an error is showing — silences a legitimately empty folder opened later.
    setError('')
    setDenied(false)
    debounce.current = setTimeout(() => {
      if (!LINK.test(term)) {
        searchSites(bid, term)
          .then((found) => live && setSites(found))
          .catch((e) => live && fail(e))
          .finally(() => live && setSitesBusy(false))
        return
      }
      // A new link replaces whatever was being browsed: a refusal must hand off for THIS link,
      // not for the site that happened to be open before it was pasted.
      setSite(null)
      setCrumbs([])
      setEntries([])
      resolveLink(bid, term)
        .then((resolved) => {
          if (!live) return
          if (resolved) {
            openResolved(resolved)
          } else {
            setSites([])
            setError(t('bidsite_link_unresolved'))
          }
        })
        .catch((e) => live && fail(e))
        .finally(() => live && setSitesBusy(false))
    }, 300)
    return () => {
      live = false
      clearTimeout(debounce.current)
    }
  }, [bid, search, isBrowse, fail, openResolved, t])

  const here = crumbs[crumbs.length - 1]

  /**
   * The FIRST crumb this pane shows, as an index into `crumbs` — everything before it is kept but
   * never rendered, and is out of reach of both the trail and the "up" row.
   *
   * 0 everywhere except a `rootAnchored` browse pane, where it is the pinned location: the browse
   * effect seeds the trail down to the pin ({@link trailFromStart}), so the pinned location is
   * crumb 2 for a folder, crumb 1 for a library and crumb 0 for a site-only pin (its libraries).
   * Hiding rather than dropping those first crumbs is what keeps every index-reading computation
   * below — {@link currentLocation}'s `crumbs[1]` drive name and `crumbs.slice(2)` folder path,
   * and `goToCrumb`'s absolute index — resolving the same drive and folder they always did.
   */
  const anchored = rootAnchored && isBrowse && Boolean(start)
  const crumbRoot = anchored && start ? trailFromStart(start).length - 1 : 0
  /** Below the pinned location, so there is somewhere to go up TO that is still the bid's own. */
  const canGoUp = anchored ? crumbs.length - 1 > crumbRoot : upRow && crumbs.length > 1

  /**
   * Clear the click-to-index selection (and any settled de-index result) on navigating to a new
   * listing — but ONLY outside index mode. In index mode a cross-folder selection is the whole
   * point (plans/BID_SITE_INDEXING.md): walking into a folder to keep choosing must not silently
   * drop what was already checked elsewhere, nor hide a de-index result the member has not yet
   * dismissed with "Done".
   */
  function clearSelectionOnNavigate(): void {
    if (browserMode === 'index') return
    setChecked(new Map())
    setDeIndexResult(null)
    setDeIndexError('')
  }

  function openSite(picked: SpSite): void {
    setSite(picked)
    setError('')
    // Cleared, not latched: in the picker a site the member cannot open is an ordinary thing to
    // click. Leaving `denied` set replaced the whole modal body — search box included — so the only
    // way back to the list was to close the dialog and reopen it.
    setDenied(false)
    setCrumbs([{ name: picked.displayName, webUrl: picked.webUrl, driveId: null, itemId: null }])
    setItemsBusy(true)
    listLibraries(bid, picked.id)
      .then(setEntries)
      .catch(fail)
      .finally(() => setItemsBusy(false))
  }

  /** Walk into a library or a folder. Files never reach here — they are links, not buttons. */
  function openFolder(entry: SpEntry): void {
    const driveId = entry.isDrive ? entry.id : (here?.driveId ?? '')
    const itemId = entry.isDrive ? 'root' : entry.id
    if (!driveId) return
    setError('')
    setDenied(false)
    clearSelectionOnNavigate()
    setCrumbs((prev) => [...prev, { name: entry.name, webUrl: entry.webUrl, driveId, itemId }])
    setItemsBusy(true)
    listChildren(bid, driveId, itemId)
      .then(setEntries)
      .catch(fail)
      .finally(() => setItemsBusy(false))
  }

  function goToCrumb(index: number): void {
    const crumb = crumbs[index]
    if (!crumb || index === crumbs.length - 1) return
    setCrumbs((prev) => prev.slice(0, index + 1))
    setError('')
    setDenied(false)
    clearSelectionOnNavigate()
    // Back at the site's libraries, a selection is dropped even in index mode: the next library
    // opened may be a DIFFERENT drive, and an index run sends one drive id for the whole selection.
    if (crumb.driveId === null) setChecked(new Map())
    setItemsBusy(true)
    const load =
      crumb.driveId === null || crumb.itemId === null
        ? site
          ? listLibraries(bid, site.id)
          : Promise.resolve([])
        : listChildren(bid, crumb.driveId, crumb.itemId)
    load
      .then(setEntries)
      .catch(fail)
      .finally(() => setItemsBusy(false))
  }

  /**
   * The "up one level" row. Usually the crumb before this one — but a folder crumb that holds a
   * whole path ("niobe/exports", a pin or a reopened destination) has no crumb for its parent, so
   * the parent is resolved from the folder's own address minus its last segment and replaces it in
   * the trail. Should that fail, the library is still one step up: better than a dead row.
   */
  function goUp(): void {
    const crumb = here
    const segments = crumb ? crumb.name.split('/').filter(Boolean) : []
    const lastSlash = crumb ? crumb.webUrl.replace(/\/+$/, '').lastIndexOf('/') : -1
    if (!crumb || crumbs.length < 3 || segments.length < 2 || lastSlash < 0 || !crumb.driveId) {
      goToCrumb(crumbs.length - 2)
      return
    }
    const parentPath = segments.slice(0, -1).join('/')
    const parentUrl = crumb.webUrl.replace(/\/+$/, '').slice(0, lastSlash)
    const driveId = crumb.driveId
    setError('')
    setDenied(false)
    clearSelectionOnNavigate()
    setItemsBusy(true)
    resolveLink(bid, parentUrl)
      .then((r) => {
        const itemId = r?.item?.id
        if (!itemId || r.drive?.id !== driveId) {
          // Unresolvable parent: fall back to the library, the crumb before this one.
          const library = crumbs[crumbs.length - 2]!
          setCrumbs((prev) => prev.slice(0, -1))
          return listChildren(bid, driveId, library.itemId ?? 'root').then(setEntries)
        }
        const parent: Crumb = {
          name: parentPath,
          label: segments[segments.length - 2],
          webUrl: r.item?.webUrl ?? parentUrl,
          driveId,
          itemId,
        }
        setCrumbs((prev) => [...prev.slice(0, -1), parent])
        return listChildren(bid, driveId, itemId).then(setEntries)
      })
      .catch(fail)
      .finally(() => setItemsBusy(false))
  }

  /**
   * "+ New folder" (canCreateFolder), submitted from its PromptModal. A name already listed HERE is
   * not a new folder to make — walk into it instead of asking Graph to create something that
   * already exists (this is also the common, expected case: re-picking "exports" a second time
   * should just re-open it).
   */
  function createNewFolder(name: string): void {
    setCreatingFolder(false)
    if (!here?.driveId || !here.itemId) return
    const trimmed = name.trim()
    const already = entries.find((e) => e.isFolder && e.name.toLowerCase() === trimmed.toLowerCase())
    if (already) {
      openFolder(already)
      return
    }
    setError('')
    createFolder(bid, here.driveId, here.itemId, trimmed)
      .then((entry) => openFolder({ ...entry, isFolder: true, isDrive: false }))
      .catch(fail)
  }

  /** Enter index mode ("Manage bid index"). Checkboxes appear and the selection starts surviving
   *  navigation — see {@link clearSelectionOnNavigate}. */
  function enterIndexMode(): void {
    setBrowserMode('index')
  }

  /**
   * Leave index mode ("Done") — from the idle four-button bar, or from either action's settled
   * state. Clears every bit of state that action left behind, so re-entering starts clean rather
   * than showing a stale result from a run that has already been dealt with.
   */
  function exitIndexMode(): void {
    setBrowserMode('browse')
    setChecked(new Map())
    setIndexingIds(new Set())
    setIndexJobId(null)
    setIndexStatus(null)
    setIndexError('')
    setCancelError('')
    setShowResultModal(false)
    setDeIndexResult(null)
    setDeIndexError('')
  }

  /** "Discard selected" — clear the selection without acting on it, staying in index mode. */
  function discardSelection(): void {
    setChecked(new Map())
  }

  /**
   * Toggle one row's checkbox. Stores the row's own descriptor, not just its id: `entries` holds
   * only the CURRENTLY open folder, so once the selection survives navigating away from this
   * folder (index mode), the id alone would be nothing to look a name/isFolder back up from.
   */
  function toggleChecked(entry: SpEntry): void {
    setChecked((prev) => {
      const next = new Map(prev)
      if (next.has(entry.id)) next.delete(entry.id)
      else next.set(entry.id, { itemId: entry.id, name: entry.name, isFolder: entry.isFolder })
      return next
    })
  }

  /** Poll one click-to-index job until it leaves `running`. */
  function pollIndex(jobId: string): void {
    indexJob(bid, jobId)
      .then((job) => {
        setIndexStatus(job)
        if (job.status === 'running') {
          pollTimer.current = setTimeout(() => pollIndex(jobId), 1500)
        } else {
          setIndexJobId(null)
          onIndexed?.()
        }
      })
      .catch((e) => {
        setIndexError(e instanceof Error ? e.message : String(e))
        setIndexJobId(null)
      })
  }

  /**
   * "Interrupt indexation" — ask the running job to stop before its next file. Fire-and-forget
   * against the poller: the run's own `result.stopped` will read `'cancelled'` once it settles,
   * which the existing poll (already running) picks up on its own — this only needs to report a
   * failure of the REQUEST itself (e.g. a permission or network problem), not the interruption.
   */
  function interruptIndexing(): void {
    if (!indexJobId) return
    setCancelBusy(true)
    setCancelError('')
    cancelIndex(bid, indexJobId)
      .catch((e) => setCancelError(e instanceof Error ? e.message : String(e)))
      .finally(() => setCancelBusy(false))
  }

  /**
   * A row's live state while THIS run processes it — looked up by name against the job's own
   * `progress` (services.ts#SpIndexProgressEntry), exactly as plans/BID_SITE_INDEXING.md
   * describes, but gated by id through `indexingIds` first: a bare name match would also catch an
   * unrelated file of the same name in a different folder (the very collision `bidSourceId` was
   * fixed to avoid — plans/BID_SITE_INDEXING.md). A folder's own row has no such entry (only the
   * files it expanded to do), so it reads as permanently 'queued' for the run's duration — the same
   * "no rollup" limitation {@link IndexedGlyph} already accepts.
   */
  function rowIndexState(entry: SpEntry): SpIndexProgressEntry['outcome'] | 'queued' | null {
    if (!indexingIds.has(entry.id)) return null
    const found = indexStatus?.progress.find((p) => p.name === entry.name)
    return found ? found.outcome : 'queued'
  }

  /** The checked rows — across every folder visited in this index-mode session — as a
   *  click-to-index/de-index selection. Assumes one drive (plans/BID_SITE_INDEXING.md scopes
   *  cross-folder selection to the bid's own pinned library, not a cross-library one). */
  function checkedSelection(): SpIndexSelection[] {
    return [...checked.values()]
  }

  /**
   * Send the checked rows — files as themselves, folders walked recursively by the backend (the
   * same OBO read this pane already used to show them) — into the bid's own KB.
   */
  function startIndexing(): void {
    if (!here?.driveId || checked.size === 0) return
    const items = checkedSelection()
    // Snapshot BEFORE clearing `checked` below — {@link rowIndexState} keys off this, not the
    // (now empty) live selection, so the per-row progress for THIS run keeps working.
    setIndexingIds(new Set(checked.keys()))
    setIndexError('')
    setIndexStatus(null)
    startIndex(bid, here.driveId, items)
      .then(({ jobId }) => {
        setChecked(new Map())
        setIndexJobId(jobId)
        pollIndex(jobId)
      })
      .catch((e) => setIndexError(e instanceof Error ? e.message : String(e)))
  }

  /** Ask for confirmation before de-indexing — the opposite write, same checkboxes. */
  function startDeIndexing(): void {
    if (!here?.driveId || checked.size === 0) return
    setConfirmingDeIndex(true)
  }

  /**
   * Remove the checked rows from the bid's own KB, one at a time — the route takes a single item,
   * a folder cascading server-side exactly like an index run walks it. Synchronous: there is no
   * download/parse/embed behind a de-index, so this simply awaits each call in turn.
   */
  async function confirmDeIndex(): Promise<void> {
    setConfirmingDeIndex(false)
    if (!here || !here.driveId || !here.itemId) return
    const driveId = here.driveId
    const itemId = here.itemId
    const items = checkedSelection()
    setDeIndexBusy(true)
    setDeIndexError('')
    setDeIndexResult(null)
    try {
      let deleted = 0
      for (const item of items) {
        deleted += (await deIndex(bid, driveId, item)).deleted
      }
      setChecked(new Map())
      setDeIndexResult(deleted)
      // Refresh the listing so the "already indexed" glyphs revert for what was just removed —
      // only THIS folder's, the one the request-de-index can actually see right now.
      setEntries(await listChildren(bid, driveId, itemId))
      onIndexed?.()
    } catch (e) {
      setDeIndexError(e instanceof Error ? e.message : String(e))
    } finally {
      setDeIndexBusy(false)
    }
  }

  /**
   * The location to pin: the site itself (its libraries — `select` only), a library, or a folder
   * in one. A `browse` pane with `onSelect` (the export destination picker) is choosing somewhere
   * to write, so the site level is not a location there.
   */
  function currentLocation(): BrowserLocation | null {
    if (!site || !here) return null
    if (!here.driveId || !here.itemId) {
      return isBrowse ? null : { site, folderPath: '', webUrl: site.webUrl }
    }
    // Crumb 0 is the site and crumb 1 the library; anything after is the folder path.
    const driveName = crumbs[1]?.name ?? ''
    const folderPath = crumbs
      .slice(2)
      .map((c) => c.name)
      .join('/')
    return {
      site,
      driveId: here.driveId,
      driveName,
      itemId: here.itemId,
      folderPath,
      webUrl: here.webUrl,
    }
  }

  // Confirm/cancel used to be `select`-only; `browse` + `onSelect` (the export destination picker)
  // now gets the same bar — `currentLocation()` already works in `browse` mode, needing only
  // `site` + `here`, both populated by the browse-mode effect above.
  const chosen = !isBrowse || onSelect ? currentLocation() : null

  // The index-mode action bar's sub-state — mutually exclusive, and in this priority order: a run
  // in flight always wins, then its settled result, then de-indexing's own busy/settled pair, and
  // only once none of those apply does the plain four-button bar (plans/BID_SITE_INDEXING.md) show.
  const indexRunning = Boolean(indexJobId) || indexStatus?.status === 'running'
  const indexSettled = !indexRunning && indexStatus != null
  const deindexSettled = !deIndexBusy && (deIndexResult !== null || deIndexError !== '')
  const idleActionBar = !indexRunning && !indexSettled && !deIndexBusy && !deindexSettled
  const indexPct =
    indexStatus && indexStatus.totalFiles
      ? Math.min(100, Math.round((indexStatus.progress.length / indexStatus.totalFiles) * 100))
      : 0

  // No sign-in gate before the lists: the pane's only two waits are its own fetches, and each says
  // so inside the list it is filling (`sitesBusy` / `itemsBusy`). Anything worse than "loading" —
  // SharePoint refusing, or the backend failing to reach it — arrives as `denied` or `error` below.
  if (denied) {
    // A pasted link SharePoint refused: its own address is where access is asked for.
    const pasted = !isBrowse && LINK.test(search.trim()) ? search.trim() : ''
    const target = start?.webUrl || here?.webUrl || pasted
    const request = target ? requestAccessUrl(target) : null
    return (
      <div className="spb-denied">
        <p className="spb-denied-title">{t('bidsite_denied')}</p>
        <p className="spb-note">{t('bidsite_denied_note')}</p>
        {request && (
          <a className="mini ok" href={request} target="_blank" rel="noopener noreferrer">
            {t('bidsite_request_access')}
          </a>
        )}
      </div>
    )
  }

  return (
    <div className={`spb${isBrowse ? ' spb-inline' : ''}`}>
      {!isBrowse && (
        <div className="spb-sites">
          <div className="spb-search">
            <IconSearch />
            <input
              type="search"
              autoFocus
              value={search}
              aria-label={t('bidsite_search_sites')}
              placeholder={t('bidsite_search_sites')}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className="spb-sitelist" aria-label={t('bidsite_sites')}>
            {sitesBusy && <li className="spb-note">{t('loading')}</li>}
            {!sitesBusy && !error && sites.length === 0 && (
              <li className="spb-note">{search.trim() ? t('bidsite_no_site') : t('bidsite_type_term')}</li>
            )}
            {!sitesBusy &&
              sites.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={`spb-siterow${site?.id === s.id ? ' is-current' : ''}`}
                    aria-current={site?.id === s.id ? 'true' : undefined}
                    onClick={() => openSite(s)}
                  >
                    {s.displayName}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}

      <div className="spb-main">
        <nav className="spb-crumbs" aria-label={t('bidsite_path')}>
          {crumbs.length === 0 ? (
            <span className="spb-note">{t('bidsite_pick_site')}</span>
          ) : (
            // Sliced, not re-indexed: `index` stays the crumb's position in the real trail, so
            // `goToCrumb` keeps addressing the same level whether or not a root is hidden.
            crumbs.slice(crumbRoot).map((c, i) => {
              const index = crumbRoot + i
              const current = index === crumbs.length - 1
              // The export picker spells the pinned path out in full; the anchored pane names it.
              const label = rootAnchored ? (c.label ?? c.name) : c.name
              return (
                <span key={`${c.name}-${index}`} className="spb-crumb">
                  {i > 0 && <span aria-hidden="true">›</span>}
                  {current && rootAnchored && c.webUrl ? (
                    // Anchored pane: the current crumb is clickable too — it opens where we ARE.
                    <a
                      href={c.webUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-current="page"
                      title={t('bidsite_open_here')}
                    >
                      {label}
                      <span className="spb-crumb-ext" aria-hidden="true">
                        ↗
                      </span>
                      <span className="visually-hidden">{t('bidsite_opens_new_tab')}</span>
                    </a>
                  ) : current ? (
                    <span aria-current="page">{label}</span>
                  ) : (
                    <button type="button" onClick={() => goToCrumb(index)}>
                      {label}
                    </button>
                  )}
                </span>
              )
            })
          )}
          {/* The keyboard-reachable equivalent of right-clicking a folder: open where we ARE. An
              anchored pane already does that from its current crumb. */}
          {!rootAnchored && here?.webUrl && (
            <a
              className="spb-openhere"
              href={here.webUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={t('bidsite_open_here')}
            >
              {t('bidsite_open_here')}
            </a>
          )}
          {canCreateFolder && (
            <button
              type="button"
              className="spb-newfolder"
              disabled={!here?.driveId}
              onClick={() => setCreatingFolder(true)}
            >
              {t('bidsite_new_folder_btn')}
            </button>
          )}
        </nav>

        <ul className="spb-items" aria-label={t('bidsite_items')} aria-busy={itemsBusy}>
          {itemsBusy && <li className="spb-note">{t('loading')}</li>}
          {/* "Up one level" — the list's own way back, so climbing out of a folder is not a trip to
              the breadcrumb. First row, and only below the pinned folder: this pane never walks
              above the bid's own root, which is exactly where {@link canGoUp} stops. It is neither
              selectable nor indexable — it is not an item, it is a direction — so it carries no
              checkbox and no index glyph even in index mode. */}
          {!itemsBusy && canGoUp && (
            <li>
              <button
                type="button"
                className="spb-row spb-folder spb-up"
                onClick={goUp}
              >
                <IconFolderUp />
                <span className="spb-name">{t('bidsite_up')}</span>
              </button>
            </li>
          )}
          {!itemsBusy && crumbs.length === 0 && <li className="spb-note">{t('bidsite_pick_site')}</li>}
          {/* `!error` matters: a failed listing leaves `entries` empty, and saying "Nothing here."
              about a folder we could not read is a lie about the member's own material. When
              something went wrong the alert below is the whole answer. */}
          {!itemsBusy && crumbs.length > 0 && entries.length === 0 && !error && (
            <li className="spb-note">{t('bidsite_empty')}</li>
          )}
          {!itemsBusy &&
            entries.map((entry) => {
              // Checkboxes only in index mode (plans/BID_SITE_INDEXING.md): plain browsing is an
              // ordinary site browser, with no selection chrome at all.
              // Not at a site's libraries level: indexing reads ONE drive, and a library row is a
              // drive, not an item in one — open it first.
              const indexable = isBrowse && canIndex && browserMode === 'index' && Boolean(here?.driveId)
              // Disabled whenever the bar isn't showing the plain four-button (idle) state —
              // a run in flight already snapshotted its own selection, and a just-settled result
              // is best looked at before the member starts building the next one.
              const checkDisabled = !idleActionBar
              const rowState = indexable ? rowIndexState(entry) : null
              return entry.isFolder ? (
                <li key={entry.id} className={indexable ? 'spb-li' : undefined}>
                  {indexable && (
                    <input
                      type="checkbox"
                      className="spb-check"
                      aria-label={t('bidsite_index_pick').replace('{name}', entry.name)}
                      checked={checked.has(entry.id)}
                      disabled={checkDisabled}
                      onChange={() => toggleChecked(entry)}
                    />
                  )}
                  {/* A folder is a BUTTON: its click navigates in place. Right-click opens it in
                      SharePoint instead — the gesture the dashboard's browser is asked for. */}
                  <button
                    type="button"
                    className="spb-row spb-folder"
                    onClick={() => openFolder(entry)}
                    onContextMenu={(e) => {
                      if (!entry.webUrl) return
                      e.preventDefault()
                      window.open(entry.webUrl, '_blank', 'noopener,noreferrer')
                    }}
                  >
                    <IconFolder />
                    <span className="spb-name">{entry.name}</span>
                    {rowState && <RowIndexState state={rowState} />}
                  </button>
                </li>
              ) : (
                <li key={entry.id} className={indexable ? 'spb-li' : undefined}>
                  {indexable && (
                    <input
                      type="checkbox"
                      className="spb-check"
                      aria-label={t('bidsite_index_pick').replace('{name}', entry.name)}
                      checked={checked.has(entry.id)}
                      disabled={checkDisabled}
                      onChange={() => toggleChecked(entry)}
                    />
                  )}
                  {/* A file is a LINK, so clicking it opens SharePoint's own viewer in a new tab —
                      and middle-click / "open in new tab" work without us reimplementing them.
                      In select mode there is nothing to open INTO, but the file still says where
                      you are, so it is listed and muted rather than hidden. */}
                  {onPickFile ? (
                    // The export picker: a click takes the file's NAME, it never opens the file.
                    <button
                      type="button"
                      className="spb-row spb-file"
                      title={pickFileTitle ?? t('bidsite_use_name')}
                      onClick={() => onPickFile(entry, here?.driveId ?? '')}
                    >
                      <FileIcon name={entry.name} />
                      <span className="spb-name">{entry.name}</span>
                    </button>
                  ) : isBrowse && entry.webUrl ? (
                    <a
                      className="spb-row spb-file"
                      href={entry.webUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FileIcon name={entry.name} />
                      <span className="spb-name">{entry.name}</span>
                      {rowState ? (
                        <RowIndexState state={rowState} />
                      ) : (
                        entry.indexed && <IndexedGlyph label={t('bidsite_indexed')} />
                      )}
                    </a>
                  ) : (
                    <span className="spb-row spb-file is-inert">
                      <FileIcon name={entry.name} />
                      <span className="spb-name">{entry.name}</span>
                      {rowState ? (
                        <RowIndexState state={rowState} />
                      ) : (
                        entry.indexed && <IndexedGlyph label={t('bidsite_indexed')} />
                      )}
                    </span>
                  )}
                </li>
              )
            })}
        </ul>

        {isBrowse && canIndex && (
          <div className="spb-indexbar" role="group" aria-label={t('bidsite_index_group')}>
            {browserMode === 'browse' ? (
              // Entry point, bottom-left (plans/BID_SITE_INDEXING.md) — plain browsing offers no
              // selection chrome at all; this is the only control that does anything with it.
              // Disabled at a site's libraries level (a site-only pin's top): indexing needs a drive,
              // and opening a library gives it one.
              <button type="button" className="mini ghost" disabled={!here?.driveId} onClick={enterIndexMode}>
                {t('bidsite_manage_index')}
              </button>
            ) : indexRunning ? (
              <>
                <button
                  type="button"
                  className="mini ghost"
                  disabled={cancelBusy || Boolean(indexStatus?.cancelRequested)}
                  onClick={interruptIndexing}
                >
                  {t('bidsite_index_interrupt')}
                </button>
                <div className="spb-progress-wrap">
                  <div
                    className="spb-progress-bar"
                    style={{ width: `${indexPct}%` }}
                    role="progressbar"
                    aria-valuenow={indexPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={t('bidsite_index_progress_bar_label')}
                  />
                </div>
                <span className="spb-indexstatus" role="status" aria-live="polite">
                  {indexStatus && typeof indexStatus.totalFiles === 'number'
                    ? t('bidsite_index_progress')
                        .replace('{done}', String(indexStatus.progress.length))
                        .replace('{total}', String(indexStatus.totalFiles))
                    : t('bidsite_indexing')}
                </span>
              </>
            ) : indexStatus && indexSettled ? (
              <>
                <button type="button" className="mini ghost" onClick={() => setShowResultModal(true)}>
                  {t('bidsite_index_result_link')}
                </button>
                <span className="spb-indexstatus" role="status" aria-live="polite">
                  {indexStatus.result &&
                    t('bidsite_index_done')
                      .replace('{ingested}', String(indexStatus.result.ingested))
                      .replace('{skipped}', String(indexStatus.result.skipped))
                      .replace('{failed}', String(indexStatus.result.failed))}
                  {indexStatus.result?.stopped === 'chunk_cap' &&
                    ` · ${t('bidsite_index_stopped_cap').replace('{n}', String(indexStatus.result.notAttempted))}`}
                  {indexStatus.result?.stopped === 'file_limit' && ` · ${t('bidsite_index_stopped_filelimit')}`}
                  {indexStatus.result?.stopped === 'cancelled' &&
                    ` · ${t('bidsite_index_stopped_cancelled').replace('{n}', String(indexStatus.result.notAttempted))}`}
                </span>
                <button type="button" className="mini ok" onClick={exitIndexMode}>
                  {t('bidsite_mode_done')}
                </button>
              </>
            ) : deIndexBusy ? (
              <span className="spb-indexstatus" role="status" aria-live="polite">
                {t('bidsite_deindexing')}
              </span>
            ) : deindexSettled ? (
              <>
                <span className="spb-indexstatus" role="status" aria-live="polite">
                  {deIndexResult !== null && t('bidsite_deindex_done').replace('{n}', String(deIndexResult))}
                </span>
                <button type="button" className="mini ok" onClick={exitIndexMode}>
                  {t('bidsite_mode_done')}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="mini ok" disabled={checked.size === 0} onClick={startIndexing}>
                  {t('bidsite_index_btn').replace('{n}', String(checked.size))}
                </button>
                <button
                  type="button"
                  className="mini ghost"
                  disabled={checked.size === 0}
                  onClick={discardSelection}
                >
                  {t('bidsite_index_discard_btn').replace('{n}', String(checked.size))}
                </button>
                <button
                  type="button"
                  className="mini ghost"
                  disabled={checked.size === 0}
                  onClick={startDeIndexing}
                >
                  {t('bidsite_deindex_btn').replace('{n}', String(checked.size))}
                </button>
                <button type="button" className="mini ghost" onClick={exitIndexMode}>
                  {t('bidsite_mode_done')}
                </button>
              </>
            )}
            {(indexError || indexStatus?.status === 'error') && (
              <span className="spb-error" role="alert">
                {t('bidsite_index_error')} {indexError || indexStatus?.error}
              </span>
            )}
            {cancelError && (
              <span className="spb-error" role="alert">
                {t('bidsite_index_cancel_error')} {cancelError}
              </span>
            )}
            {deIndexError && (
              <span className="spb-error" role="alert">
                {t('bidsite_deindex_error')} {deIndexError}
              </span>
            )}
            {/* Announced on every mode switch — the checkbox/button chrome that appears otherwise
                has no non-visual cue of its own. */}
            <span className="visually-hidden" role="status" aria-live="polite">
              {browserMode === 'index' ? t('bidsite_mode_announce_index') : t('bidsite_mode_announce_browse')}
            </span>
          </div>
        )}

        {showResultModal && indexStatus && (
          <IndexResultModal job={indexStatus} onClose={() => setShowResultModal(false)} />
        )}

        {confirmingDeIndex && (
          <ConfirmModal
            title={t('bidsite_deindex_title')}
            message={t('bidsite_deindex_msg')}
            confirmLabel={t('bidsite_deindex_confirm')}
            danger
            onConfirm={() => void confirmDeIndex()}
            onClose={() => setConfirmingDeIndex(false)}
          />
        )}

        {error && (
          <p className="spb-error" role="alert">
            {error}
          </p>
        )}

        {footer}

        {(!isBrowse || onSelect) && (
          <div className="spb-acts">
            <span className="spb-here" title={chosen?.webUrl ?? ''}>
              {chosen ? locationLabel(chosen) : t('bidsite_pick_folder')}
            </span>
            <button type="button" className="mini ghost" onClick={onCancel}>
              {t('cancel')}
            </button>
            <button
              type="button"
              className="mini ok"
              disabled={!chosen || busy}
              onClick={() => chosen && onSelect?.(chosen)}
            >
              {t('bidsite_confirm')}
            </button>
          </div>
        )}

        {creatingFolder && (
          <PromptModal
            title={t('bidsite_new_folder_title')}
            label={t('bidsite_new_folder_label')}
            onSubmit={(name) => createNewFolder(name)}
            onClose={() => setCreatingFolder(false)}
          />
        )}
      </div>
    </div>
  )
}

/**
 * "Site › Library › folder/path" — a chosen location spelled out in full. Exported for the export
 * destination picker (ExportMenu.tsx), the caller that needs to show a chosen `BrowserLocation`
 * back to the member once its own dialog has closed. The dashboard row no longer reads this way:
 * it names the site and nothing else (BidSiteCard.tsx), the path being the pane's own business.
 */
export function locationLabel(location: BrowserLocation): string {
  return [location.site.displayName, location.driveName, location.folderPath]
    .filter(Boolean)
    .join(' › ')
}

/**
 * The "up one level" row's glyph — the same folder as {@link IconFolder} with an arrow out of it,
 * so the row reads as a folder in the list it heads rather than as a bare arrow. Decorative: the
 * button around it carries the words.
 */
function IconFolderUp() {
  return (
    <svg className="nav-ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <line x1="12" y1="17" x2="12" y2="11" />
      <polyline points="9.5 13.5 12 11 14.5 13.5" />
    </svg>
  )
}

/**
 * The "already indexed" status glyph — icon + text, never colour alone (a11y): a check mark
 * `aria-hidden`, paired with a `title` for a sighted mouse hover and a `visually-hidden` span
 * carrying the same words for a screen reader.
 */
function IndexedGlyph({ label }: { label: string }) {
  return (
    <span className="spb-indexed" title={label}>
      <svg className="nav-ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M20 6 9 17l-5-5" />
      </svg>
      <span className="visually-hidden">{label}</span>
    </span>
  )
}

/** Icon + a WORD, never colour alone (a11y) — same rule as {@link IndexedGlyph}. */
const ROW_STATE_GLYPH: Record<'queued' | 'ingested' | 'skipped' | 'failed', string> = {
  queued: '…',
  ingested: '✓',
  skipped: '–',
  failed: '✕',
}

/**
 * A row's live state while a click-to-index run this pane started is in flight or has just
 * settled (plans/BID_SITE_INDEXING.md). The label itself carries the word, so nothing here
 * depends on colour alone reaching the member.
 */
function RowIndexState({ state }: { state: 'queued' | 'ingested' | 'skipped' | 'failed' }) {
  const { t } = useLang()
  return (
    <span className={`spb-rowstate spb-rowstate-${state}`}>
      <span aria-hidden="true">{ROW_STATE_GLYPH[state]}</span>{' '}
      <span>{t(`bidsite_row_${state}` as 'bidsite_row_queued')}</span>
    </span>
  )
}
